import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrganizationCguEnforcement,
  OrganizationDto,
  UpdateOrganizationCguDto,
} from 'models/organization/dto/organization.dto';
import { useApiClient } from '../../../apiClient';
import { useSelectedOrganization } from './useSelectedOrganization';

type UpdateOrganizationCguResponse = {
  cguVersion: number;
  cguEnforcement: OrganizationCguEnforcement;
};

/**
 * POST /organizations/:id/cgu — set or clear the org-level default CGU and/or
 * change the enforcement mode (off / soft / strict).
 *
 * Pass `cguText: null` to clear (preserves the enforcement mode but resolveCgu
 * will return null because there's no text to display). Pass `cguEnforcement`
 * alone to flip the mode without touching cguText/cguVersion.
 *
 * Server returns `{ cguVersion, cguEnforcement }` — never echoes back the text
 * body. We refresh the selected organization so any version-dependent UI updates.
 */
export const useUpdateOrganizationCgu = (organizationId: string) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { selectedOrganization, setSelectedOrganization } =
    useSelectedOrganization();

  return useMutation({
    mutationKey: ['updateOrganizationCgu', organizationId],
    mutationFn: (body: UpdateOrganizationCguDto) =>
      apiClient.fetchApi<
        UpdateOrganizationCguResponse,
        UpdateOrganizationCguDto
      >({
        method: 'POST',
        endpoint: `organizations/${organizationId}/cgu`,
        body,
      }),
    onSuccess: ({ cguVersion }) => {
      // Sync the selected-organization store with the new version. Membership
      // types that override their own CGU are independent — we don't invalidate
      // their cache here. cguText itself is fetched separately via /cgu when
      // operators reopen the editor.
      if (selectedOrganization?.id === organizationId) {
        setSelectedOrganization({
          ...selectedOrganization,
          cguVersion,
        } as OrganizationDto);
      }
      // Bust any cached fetches of GET /cgu for this org (org-fallback returns
      // change for every type that doesn't have an override).
      queryClient.invalidateQueries({ queryKey: ['cgu', organizationId] });
    },
  });
};
